/**
 * Pure formatting helpers shared across Console pages.
 * No React and no domain dependencies live here.
 */

import { format, parseISO } from 'date-fns';

/**
 * Formats an ISO date string into a short, human-readable date.
 * Returns an em dash when the input is missing.
 */
export const formatDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) {
    return '—';
  }

  return format(parseISO(isoDate), 'dd/MM/yyyy');
};

/**
 * Formats an ISO date string including the time of day.
 */
export const formatDateTime = (isoDate: string | null | undefined): string => {
  if (!isoDate) {
    return '—';
  }

  return format(parseISO(isoDate), 'dd/MM/yyyy HH:mm');
};

/**
 * Formats a numeric amount as a currency string.
 * The currency code drives the locale so MXN, COP, CLP and PEN all render
 * with their expected separators and symbol.
 */
export const formatCurrency = (amount: number, currency: string): string => {
  const localeByCurrency: Record<string, string> = {
    MXN: 'es-MX',
    COP: 'es-CO',
    CLP: 'es-CL',
    PEN: 'es-PE',
  };

  const locale = localeByCurrency[currency] ?? 'es-MX';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Formats an integer with thousands separators.
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('es-MX').format(value);
};
